@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class)

package com.trevorwiebe.timesheet.invoicing.presentation

import com.trevorwiebe.timesheet.theme.warningYellowText
import com.trevorwiebe.timesheet.theme.successGreenText
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.ChipItem
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceDisplayStatus
import androidx.compose.ui.graphics.Color
import com.trevorwiebe.timesheet.loads.presentation.DocumentViewer
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.material3.TextButton
import androidx.compose.material3.Card
import androidx.compose.material3.Icon
import org.jetbrains.compose.resources.painterResource
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.baseline_receipt_long_24
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.shape.RoundedCornerShape
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.core.presentation.common.DraftActionButton
import com.trevorwiebe.timesheet.core.presentation.common.DraftButtonStyle
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.clickable
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveWidth
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceLine
import com.trevorwiebe.timesheet.core.domain.formatPercent
import kotlinx.datetime.LocalDate
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.presentation.admin.EditLoadDialog
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue

@Composable
internal fun InvoiceDetailContent(invoice: Invoice, state: InvoicingState, openPdf: () -> Unit, onEvent: (InvoicingEvent) -> Unit) {
    BackHandler { onEvent(InvoicingEvent.OnCloseInvoice) }
    var editLoad by remember { mutableStateOf<Load?>(null) }
    val viewPdf = {
        openPdf()
        onEvent(InvoicingEvent.OnViewPdfClick)
    }
    Scaffold(
        topBar = {
            TopBar(
                title = "Invoice ${invoice.numberLabel}",
                backIcon = { BackIcon() },
                onBack = { onEvent(InvoicingEvent.OnCloseInvoice) },
                actions = {
                    TextButton(
                        onClick = viewPdf,
                        enabled = !state.isGeneratingPdf,
                    ) {
                        Text(
                            text = if (state.isGeneratingPdf) "Preparing PDF…" else "View PDF",
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                },
            )
        },
        bottomBar = {
            ResponsiveWidth {
                Row(
                    modifier = Modifier.fillMaxWidth().navigationBarsPadding().padding(12.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    if (invoice.isDraft) {
                        DraftActionButton(
                            text = "Delete draft",
                            style = DraftButtonStyle.DESTRUCTIVE,
                            onClick = { onEvent(InvoicingEvent.OnRequestDelete) },
                        )
                        DraftActionButton(
                            text = "Mark as sent",
                            style = DraftButtonStyle.OUTLINED,
                            loading = state.isMarkingSent,
                            enabled = !state.isMarkingSent,
                            onClick = { onEvent(InvoicingEvent.OnRequestMarkSent) },
                        )
                        DraftActionButton(
                            text = "Email Invoice",
                            style = DraftButtonStyle.FILLED,
                            enabled = state.sendDialog == null,
                            onClick = { onEvent(InvoicingEvent.OnSendClick) },
                        )
                    } else {
                        DraftActionButton(
                            text = "Email Invoice",
                            style = if (invoice.balance > 0) DraftButtonStyle.OUTLINED else DraftButtonStyle.FILLED,
                            enabled = state.sendDialog == null,
                            onClick = { onEvent(InvoicingEvent.OnSendClick) },
                        )
                        if (invoice.balance > 0) {
                            DraftActionButton(
                                text = "Receive payment",
                                style = DraftButtonStyle.FILLED,
                                onClick = { onEvent(InvoicingEvent.OnReceivePaymentClick(invoice.customerId)) },
                            )
                        }
                    }
                }
            }
        },
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            LazyColumn(Modifier.fillMaxSize()) {
                item { InvoiceHeader(invoice) }
                if (state.selectedInvoiceIsOutdated) {
                    item { OutdatedBanner(onUpdate = { onEvent(InvoicingEvent.OnRequestUpdateInvoice) }) }
                }
                items(invoice.lines, key = { it.loadId }) { line ->
                    InvoiceLineRow(line, onClick = state.loadFor(line)?.let { load -> { editLoad = load } })
                }
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text("Total", fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                        Text(invoice.totalFormatted, fontWeight = FontWeight.Bold)
                    }
                    HorizontalDivider()
                }
                if (!invoice.isDraft) {
                    item { PaymentsHeader() }
                    items(state.selectedInvoicePayments, key = { it.payment.id }) { row ->
                        PaymentRow(row = row, onClick = { onEvent(InvoicingEvent.OnRequestDeletePayment(row.payment.id)) })
                    }
                    item {
                        if (state.selectedInvoicePayments.isEmpty()) {
                            Text(
                                text = "No payments yet.",
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                            )
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text("Balance due", fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                            Text(invoice.balanceFormatted, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }
    }

    editLoad?.let { EditLoadDialog(it, onDismiss = { editLoad = null }) }

    NativeDialog(
        visible = state.showUpdateConfirm,
        title = "Update ${invoice.numberLabel}?",
        message = "The invoice will match its loads as they are now and be marked edited on " +
            "${Util.toFriendlyDate(state.today)}. Email it again to send the customer the corrected copy.",
        confirmText = if (state.isUpdatingInvoice) "Updating…" else "Update",
        onConfirm = { if (!state.isUpdatingInvoice) onEvent(InvoicingEvent.OnConfirmUpdateInvoice) },
        dismissText = "Cancel",
        onDismiss = { onEvent(InvoicingEvent.OnDismissUpdateInvoice) },
    )

    state.sendDialog?.let { dialog ->
        SendInvoiceDialog(invoice, dialog, state.selectedInvoiceDocuments, state.isGeneratingPdf, viewPdf, onEvent)
    }
    state.openDocument?.let { document ->
        DocumentViewer(
            document = document,
            pdf = state.documentPdfs[document.url],
            isSaving = false,
            onDismiss = { onEvent(InvoicingEvent.OnCloseDocument) },
        )
    }

    NativeDialog(
        visible = state.infoMessage != null,
        title = state.infoTitle,
        message = state.infoMessage,
        confirmText = "OK",
        onConfirm = { onEvent(InvoicingEvent.OnInfoShown) },
        onDismiss = { onEvent(InvoicingEvent.OnInfoShown) },
    )

    NativeDialog(
        visible = state.showMarkSentConfirm,
        title = "Mark ${invoice.numberLabel} as sent?",
        message = "It moves to Open so payments can be applied, and it can no longer be deleted: " +
            "sent invoices are kept for your records.",
        confirmText = if (state.isMarkingSent) "Saving…" else "Mark as sent",
        onConfirm = { if (!state.isMarkingSent) onEvent(InvoicingEvent.OnConfirmMarkSent) },
        dismissText = "Cancel",
        onDismiss = { onEvent(InvoicingEvent.OnDismissMarkSent) },
    )

    val paymentToDelete = state.paymentToDelete
    NativeDialog(
        visible = paymentToDelete != null,
        title = "Remove this payment?",
        message = paymentToDelete?.let { payment ->
            "The ${payment.amountFormatted} payment from ${Util.toFriendlyDate(payment.date)} will be taken off " +
                "every invoice it was applied to. Use this only for a payment entered by mistake."
        },
        confirmText = if (state.isDeletingPayment) "Removing…" else "Remove",
        onConfirm = { if (!state.isDeletingPayment) onEvent(InvoicingEvent.OnConfirmDeletePayment) },
        dismissText = "Cancel",
        onDismiss = { onEvent(InvoicingEvent.OnDismissDeletePayment) },
    )

    NativeDialog(
        visible = state.showDeleteConfirm,
        title = "Delete draft ${invoice.numberLabel}?",
        message = "Its ${invoice.lines.size} load(s) go back to uninvoiced so you can bill them again.",
        confirmText = if (state.isDeleting) "Deleting…" else "Delete",
        onConfirm = { if (!state.isDeleting) onEvent(InvoicingEvent.OnConfirmDelete) },
        dismissText = "Cancel",
        onDismiss = { onEvent(InvoicingEvent.OnDismissDelete) },
    )
}

@Composable
private fun InvoiceHeader(invoice: Invoice) {
    Column(Modifier.fillMaxWidth().padding(12.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = invoice.customerName,
                fontSize = 18.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            ChipItem(invoice.displayStatus.title, invoice.displayStatus.color)
        }
        Spacer(Modifier.height(4.dp))
        DetailLine("Invoice date", Util.toFriendlyDate(invoice.invoiceDate))
        if (invoice.paymentTerms.isNotBlank()) DetailLine("Terms", invoice.paymentTerms)
        invoice.sentAt?.let {
            DetailLine("Sent", Util.toFriendlyDate(it) + if (invoice.sentTo.isNotBlank()) " to ${invoice.sentTo}" else "")
        }
        DetailLine("Loads", invoice.lines.size.toString())
        invoice.editedAt?.let { EditedFlag(it) }
    }
    HorizontalDivider()
}

@Composable
private fun EditedFlag(date: LocalDate) {
    Text(
        text = "Edited on ${Util.toFriendlyDate(date)}",
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        color = warningYellowText,
        modifier = Modifier.padding(top = 4.dp),
    )
}

@Composable
private fun OutdatedBanner(onUpdate: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = "A load on this invoice changed after it was sent.",
            fontSize = 13.sp,
            color = warningYellowText,
            modifier = Modifier.weight(1f),
        )
        TextButton(onClick = onUpdate) {
            Text("Update invoice", color = MaterialTheme.colorScheme.tertiary)
        }
    }
    HorizontalDivider()
}

@Composable
private fun DetailLine(label: String, value: String) {
    Row {
        Text(label, fontSize = 14.sp, color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f), maxLines = 1)
        Text(value, fontSize = 14.sp, textAlign = TextAlign.End, modifier = Modifier.weight(1f).padding(start = 12.dp))
    }
}

@Composable
private fun InvoiceLineRow(line: InvoiceLine, onClick: (() -> Unit)?) {
    Column(
        Modifier
            .fillMaxWidth()
            .clickable(enabled = onClick != null) { onClick?.invoke() }
            .padding(horizontal = 12.dp, vertical = 8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = Util.toFriendlyDate(line.date) + if (line.loadNumber.isNotBlank()) "  #${line.loadNumber}" else "",
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            Text(line.totalFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "${line.pickup}  to  ${line.drop}",
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            if (line.po.isNotBlank()) {
                Text("PO ${line.po}", fontSize = 13.sp, modifier = Modifier.padding(start = 8.dp))
            }
        }
        ChargeLine(line.description, line.base)
        if (line.fuelSurcharge != 0) ChargeLine(fuelSurchargeLabel(line), line.fuelSurcharge)
        if (line.detention != 0) ChargeLine("Detention", line.detention)
        if (line.stopOff != 0) ChargeLine("Stop-off", line.stopOff)
        if (line.other != 0) ChargeLine("Other", line.other)
    }
    HorizontalDivider()
}

private fun fuelSurchargeLabel(line: InvoiceLine): String =
    if (line.fuelSurchargePercent > 0) "Fuel surcharge (${line.fuelSurchargePercent.formatPercent()}%)" else "Fuel surcharge"

@Composable
private fun ChargeLine(label: String, cents: Int) {
    Row {
        Text(label, fontSize = 13.sp, modifier = Modifier.weight(1f).padding(start = 8.dp))
        Text("$" + cents.formatCentsAsDollars(), fontSize = 13.sp)
    }
}

@Composable
private fun PaymentsHeader() {
    Text(
        text = "Payments",
        fontSize = 16.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(start = 12.dp, end = 12.dp, top = 12.dp, bottom = 4.dp),
    )
}

@Composable
private fun PaymentRow(row: InvoicePaymentRow, onClick: () -> Unit) {
    val payment = row.payment
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = Util.toFriendlyDate(payment.date),
                fontSize = 14.sp,
            )
            if (row.applied != payment.amount) {
                Text(
                    text = "Part of a ${payment.amountFormatted} payment",
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                )
            }
        }
        Text(row.appliedFormatted, fontSize = 14.sp)
    }
}

@Composable
private fun SendInvoiceDialog(
    invoice: Invoice,
    dialog: SendInvoiceState,
    documents: List<InvoiceLoadDocuments>,
    isPreparingPdf: Boolean,
    onViewPdf: () -> Unit,
    onEvent: (InvoicingEvent) -> Unit,
) {
    Dialog(onDismissRequest = { onEvent(InvoicingEvent.OnDismissSend) }) {
        Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
            Column(Modifier.padding(20.dp)) {
                Text("Email invoice ${invoice.numberLabel}", fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                Column(Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState())) {
                    Spacer(Modifier.height(12.dp))
                    TimesheetTextField(
                        modifier = Modifier.fillMaxWidth(),
                        text = dialog.to,
                        label = "Send to",
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        onTextChange = { onEvent(InvoicingEvent.OnSendRecipientChanged(it)) },
                    )
                    Text(
                        text = "Separate addresses with commas.",
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                        modifier = Modifier.padding(start = 4.dp, top = 4.dp),
                    )
                    Spacer(Modifier.height(16.dp))
                    Text("Attachments", fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(enabled = !isPreparingPdf, onClick = onViewPdf)
                            .padding(vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Icon(
                            painterResource(Res.drawable.baseline_receipt_long_24),
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.tertiary,
                            modifier = Modifier.padding(horizontal = 12.dp),
                        )
                        Text(
                            text = if (isPreparingPdf) "Preparing invoice ${invoice.numberLabel} PDF…" else "Invoice ${invoice.numberLabel} PDF",
                            fontSize = 14.sp,
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                    if (documents.isEmpty()) {
                        Text(
                            text = "None of these loads have documents attached.",
                            fontSize = 13.sp,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                            modifier = Modifier.padding(vertical = 4.dp),
                        )
                    }
                    documents.forEach { group ->
                        val line = group.line
                        Text(
                            text = Util.toFriendlyDate(line.date) + if (line.loadNumber.isNotBlank()) "  #${line.loadNumber}" else "",
                            fontSize = 13.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                            modifier = Modifier.padding(top = 8.dp),
                        )
                        group.attachments.forEach { attachment ->
                            SendDocumentRow(
                                name = attachment.name,
                                checked = attachment.url in dialog.documentUrls,
                                enabled = !dialog.isSending,
                                onToggle = { onEvent(InvoicingEvent.OnToggleSendDocument(attachment.url)) },
                                onOpen = { onEvent(InvoicingEvent.OnOpenDocument(attachment.url)) },
                            )
                        }
                    }
                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = if (invoice.isDraft) "Once sent, the invoice can't be deleted." else "This emails the invoice again.",
                        fontSize = 13.sp,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    )
                }
                dialog.error?.let {
                    Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp, modifier = Modifier.padding(top = 6.dp))
                }
                Spacer(Modifier.height(16.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                    TextButton(onClick = { onEvent(InvoicingEvent.OnDismissSend) }, enabled = !dialog.isSending) {
                        Text("Cancel", color = MaterialTheme.colorScheme.tertiary)
                    }
                    Spacer(Modifier.width(8.dp))
                    TimeSheetButton(
                        onClick = { onEvent(InvoicingEvent.OnConfirmSend) },
                        text = "Send",
                        modifier = Modifier,
                        loading = dialog.isSending,
                        enabled = !dialog.isSending && dialog.to.isNotBlank(),
                    )
                }
            }
        }
    }
}

/** The checkbox chooses whether the document is sent; the name opens it for review. */
@Composable
private fun SendDocumentRow(
    name: String,
    checked: Boolean,
    enabled: Boolean,
    onToggle: () -> Unit,
    onOpen: () -> Unit,
) {
    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Checkbox(
            checked = checked,
            onCheckedChange = { onToggle() },
            enabled = enabled,
            colors = CheckboxDefaults.colors(
                checkedColor = MaterialTheme.colorScheme.tertiary,
                checkmarkColor = Color.White,
            ),
        )
        Column(
            Modifier
                .weight(1f)
                .clickable(onClick = onOpen)
                .padding(vertical = 6.dp, horizontal = 4.dp),
        ) {
            Text(
                text = name,
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.tertiary,
                textDecoration = TextDecoration.Underline,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

private val InvoiceDisplayStatus.color: Color
    get() = when (this) {
        InvoiceDisplayStatus.DRAFT -> Color.Gray
        InvoiceDisplayStatus.OPEN -> Color(0xFF1976D2)
        InvoiceDisplayStatus.PARTIALLY_PAID -> warningYellowText
        InvoiceDisplayStatus.PAID -> successGreenText
    }
